import { useCallback, useState } from "react";

import { getBulletin } from "@rubi/api-generated";

import { useBulletinRow } from "@/components/alerts/add-v2/persistence/bulletinRowContext";

/**
 * The duplicate-occurrence warning, carried over from the old flow: adding an occurrence number that
 * is already on a published bulletin opens a dialog linking to it.
 *
 * The trigger is the difference. `UpdateAlert` watches `occurrenceNumbers` in an effect and diffs it
 * against a ref, which it then has to re-sync on load so a draft's existing numbers do not all warn at
 * once. Here the only writer is the field's own `onChange`, so what was added is already known and no
 * effect, ref or re-sync is needed.
 *
 * The bulletin being edited is excluded, as the old flow's `b.id !== bulletinId` does. The id comes
 * from `useBulletinRow` rather than from the form model, which deliberately has none (README
 * decisions 5 and 6) — and it is read at check time rather than subscribed to, because the only
 * caller is a click handler and in edit mode the id is set before anything renders.
 */

export interface DuplicateBulletin {
  id: number;
  occurrenceNumbers: string[];
}

export interface OccurrenceDuplicates {
  matches: DuplicateBulletin[];
  /** Call with an occurrence number the officer has just added. Fire and forget. */
  check: (occurrenceNumber: string) => void;
  dismiss: () => void;
}

export const useOccurrenceDuplicates = (): OccurrenceDuplicates => {
  const [matches, setMatches] = useState<DuplicateBulletin[]>([]);
  const row = useBulletinRow();

  const check = useCallback(
    (occurrenceNumber: string) => {
      getBulletin({
        query: {
          start: 0,
          amount: 20,
          is_draft: false,
          occurrence_number: occurrenceNumber,
        },
      })
        .then((response) => {
          // The endpoint's own match is looser than what this warns about, so the exact number is
          // confirmed here — as the old flow does.
          const found = (response.data?.items ?? []).filter(
            (bulletin) =>
              bulletin.id !== row.id() &&
              bulletin.occurrence_number.some(
                (number) =>
                  number.toLowerCase() === occurrenceNumber.toLowerCase(),
              ),
          );

          if (found.length === 0) return;

          setMatches((previous) => [
            ...previous,
            ...found
              .filter(
                (bulletin) => !previous.some(({ id }) => id === bulletin.id),
              )
              .map((bulletin) => ({
                id: bulletin.id,
                occurrenceNumbers: bulletin.occurrence_number,
              })),
          ]);
        })
        .catch((error) => {
          // Advisory only: a lookup that fails must not stop the officer adding the number.
          console.error(
            "Failed to check for duplicate occurrence numbers:",
            error,
          );
        });
    },
    [row],
  );

  return { matches, check, dismiss: () => setMatches([]) };
};
